import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import { usePublish } from '@atlaskit/rovo-triggers';
import AiGenerativeTextIcon from '@atlaskit/icon-lab/core/ai-generative-text';

import { useSmartCreationModalActions } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';

const i18n = defineMessages({
	whiteboardToLiveDocWithAi: {
		id: 'content-types-header.content-types.more-actions.whiteboard-create-live-doc-with-rovo',
		defaultMessage: 'Create live doc with Rovo',
		description:
			'The label for the more actions button that triggers whiteboard to page conversion',
	},
});

export const WHITEBOARD_TO_LIVE_DOC_PROMPT = 'Create a live doc from this whiteboard.';

export type WhiteboardToLiveDocMenuItemProps = {
	spaceKey: string;
	contentId: string;
};

export const WhiteboardToLiveDocMenuItem = ({
	spaceKey,
	contentId,
}: WhiteboardToLiveDocMenuItemProps): React.JSX.Element => {
	const { openModal } = useSmartCreationModalActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const intl = useIntl();
	const publish = usePublish('ai-mate');

	const title = intl.formatMessage(i18n.whiteboardToLiveDocWithAi);

	const handleWhiteboardToLiveDocWithAi = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'whiteboardToLiveDocWithAi',
				source: 'overflowMenu',
			},
		}).fire();

		openModal({
			entryPoint: 'whiteboardToLiveDocAiConversion',
			spaceKey,
			stagingAreaActive: true,
			content: {
				id: contentId,
				type: 'whiteboard',
			},
		});

		publish({
			type: 'chat-new',
			source: 'CONFLUENCE_WHITEBOARD_TO_PAGE_BUTTON',
			openChat: false,
			data: {
				name: title,
				dialogues: [],
				prompt: WHITEBOARD_TO_LIVE_DOC_PROMPT, // Hardcoded to en-us for now until we can ensure backend support for other locales
				contentContext: 'staging-area',
				sourceId: contentId,
			},
		});
	}, [createAnalyticsEvent, openModal, spaceKey, publish, title, contentId]);

	const item = {
		id: 'whiteboard-to-live-doc-with-ai',
		text: title,
		onClick: handleWhiteboardToLiveDocWithAi,
	};

	const { id, onClick, text } = item;

	return (
		<DropdownItem elemBefore={<AiGenerativeTextIcon label="" />} key={id} onClick={onClick}>
			{text}
		</DropdownItem>
	);
};
